// One node out of a tree, by id.
//
// Written for the recommendations panel, which is handed a node id by rgip and has to
// know what that node IS before it writes to it. Applying a condition to a node that
// already splits is a different operation from applying one to a leaf, and picking the
// wrong one silently duplicates the node's entire subtree onto both new branches.
//
// The shape is read off the tree the page already holds rather than taken from the
// finding. A review can be minutes or days old, and `TreesPageModel.loadOne` blanks a
// definition while it refetches, so the finding's idea of a node is a claim and the
// structure is the fact.
import TreeStructure from '../../../models/tree/TreeStructure';

/** The node with this id, or null when the tree does not hold one. */
export function findNode(
  structure: TreeStructure | null | undefined,
  nodeId: number
): TreeStructure | null {
  if (!structure) return null;
  if (Number(structure.id) === Number(nodeId)) return structure;
  for (const child of structure.children ?? []) {
    const found = findNode(child, nodeId);
    if (found) return found;
  }
  return null;
}

/**
 * How far this node sits from the root, the root being 0. `null` when it is not in here.
 *
 * Read off the structure rather than counted out of the node's cumulative conditions
 * string: the conditions are a rendering, and one split can contribute several predicates.
 */
export function nodeDepth(
  structure: TreeStructure | null | undefined,
  nodeId: number,
  depth = 0
): number | null {
  if (!structure) return null;
  if (Number(structure.id) === Number(nodeId)) return depth;
  for (const child of structure.children ?? []) {
    const found = nodeDepth(child, nodeId, depth + 1);
    if (found !== null) return found;
  }
  return null;
}

/**
 * Whether this node already splits, and therefore whether a condition applied to it
 * replaces something.
 *
 * Keyed on children rather than on `splitConditions`, because children are what the
 * insert path copies. A node with a condition and no children cannot arise from the
 * editor, and if one ever did, treating it as a leaf is the harmless reading.
 */
export function splitsAlready(node: TreeStructure | null | undefined): boolean {
  return !!node?.children?.length;
}
